/**
 * Query Executor Service
 * Provides a clean interface for executing SQL queries through the Electron API.
 * Uses the engine-agnostic V2 APIs.
 */

import type {
  QueryExecutionRequest,
  QueryExecutionResult,
  QueryCancelRequest,
  QueryCancelResult,
} from '../../../types/queryExecution';
import { recordKnownDatabase } from '../../connections/services/knownDatabasesTracker';

// Slice 5 (Connection Diagnostics): expose the renderer-side wrapper on a
// stable window global so E2E specs can verify the append-on-success hook
// without coupling to the workspace UI. Production renderers attaching this
// is harmless — single function reference, no PII, no behavioural impact.
declare global {
  interface Window {
    __SLICE5_QUERY_EXECUTOR__?: {
      executeQueryV2: (request: QueryExecutionRequest) => Promise<QueryExecutionResult>;
    };
  }
}

/**
 * Execute a SQL query using the engine-agnostic API
 *
 * @param request The query execution request
 * @returns Promise with engine-neutral query results
 */
export async function executeQueryV2(request: QueryExecutionRequest): Promise<QueryExecutionResult> {
  if (!window.electronAPI?.executeQueryV2) {
    return {
      success: false,
      error: { message: 'Electron API is not available' },
    };
  }

  try {
    const result = await window.electronAPI.executeQueryV2(request);
    // Slice 5 (Connection Diagnostics): a successful query against a specific
    // database confirms we can pool-open it. Append to the connection's
    // `knownDatabases` so we can render it in the smart-fallback tree when
    // listing fails (essential for contained-user shapes on Azure SQL).
    if (result.success && request.connectionId && request.database) {
      recordKnownDatabase(request.connectionId, request.database);
    }
    return result;
  } catch (error) {
    console.error('[QueryExecutor] Error executing query:', error);
    return {
      success: false,
      error: { message: error instanceof Error ? error.message : 'Unknown error executing query' },
    };
  }
}

if (typeof window !== 'undefined') {
  window.__SLICE5_QUERY_EXECUTOR__ = { executeQueryV2 };
}

/**
 * Cancel a running query using the engine-agnostic API
 *
 * @param request The cancel request with executionId
 * @returns Promise with cancel result
 */
export async function cancelQueryV2(request: QueryCancelRequest): Promise<QueryCancelResult> {
  if (!window.electronAPI?.cancelQueryV2) {
    return {
      success: false,
      error: 'Electron API is not available',
    };
  }

  try {
    const result = await window.electronAPI.cancelQueryV2(request);
    return result;
  } catch (error) {
    console.error('[QueryExecutor] Error cancelling query:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error cancelling query',
    };
  }
}
